import {useDispatch, useSelector} from "react-redux";
import React, {useEffect, useState} from "react";
import * as yup from "yup";
import {useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import PlacesAutocomplete, {geocodeByAddress} from "react-places-autocomplete";
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    FormHelperText
} from "@mui/material";
import MDTypography from "../../../components/MDTypography";
import MDBox from "../../../components/MDBox";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import MDInput from "../../../components/MDInput";
import MuiPhoneNumber from "material-ui-phone-number";
import Checkbox from "@mui/material/Checkbox";
import FramerFade from "../../../components/Animation/Fade";
import MDButton from "../../../components/MDButton";
import {
    getMultiAddressList,
    addMultiAddress,
    Logout,
    onErrorAlert,
    onSuccessAlert,
    modifyMultiAddress
} from "../../../actions";
import {noop} from "lodash";
import {useNavigate} from "react-router-dom";
import LocalStorage from "../../../utils/LocalStorage";
import PaperComponent from "../../../components/MuiDraggableDialog";
import {useTranslation} from "react-i18next";

const MultiaddressAdd = ({isAddressOpen,setIsAddressOpen,addresses,setAddresses,currentAddress = {},setCurrentAddress,mode,setMode, pageFormData={}, pageLoading ,setPageLoading}) => {

    const darkMode = useSelector(state => state?.common?.darkMode,(prev, next) => prev === next)
    const [address,setAddress] = useState('')
    const [cityValue,setCityValue] = useState('')
    const [provinceValue,setProvinceValue] = useState('')
    const [postcodeValue,setPostcodeValue] = useState('')
    const [countryValue,setCountryValue] = useState('')
    const addressFilter = ['address']
    const languageSelect = useSelector(state => state?.common?.language,(prev, next) => prev === next)
    const [hoverSelect,setHoverSelect] = useState(false)
    const [isDefault,setIsDefault] = useState(false)
    const [phone,setPhone] = useState('')
    const dispatch = useDispatch()
    const nav = useNavigate()
    const {t} = useTranslation();

    const schema = yup
        .object({
            fullName: yup.string()
                .min(3,t('CustomerPage.Full name must equal or more than 3 characters'))
                .max(32,t('CustomerPage.Full name must equal or less than 32 characters'))
                .matches(/^[^\s].*[^\s]$/, t('CustomerPage.No leading or trailing spaces allowed.'))
                .required(t('Required'))
            ,
            phoneNumber:yup.string(),
            // email: yup.string().trim().email("Email must be a valid email").required("Email is Required"),
            address1:yup.string().trim(),
            city:yup.string().trim()

        })

    const {
        register,
        reset,
        trigger,
        setError,
        handleSubmit,
        getValues,
        setValue,
        clearErrors,
        formState: {errors}
    } = useForm({
        resolver: yupResolver(schema),
        mode: "onChange"
    });

    const handleClearAll = () => {
        setMode('Add')
        setCurrentAddress({})
        setIsAddressOpen(false)
        reset()
        clearErrors()
        setAddress('')
        setCityValue('')
        setProvinceValue('')
        setPostcodeValue('')
        setCountryValue('')
        setIsDefault(false)
        setPhone('')

    }
    const onError = (msg, code) => {
        dispatch(onErrorAlert(msg))
        setPageLoading(false)
        if (code === 401) {
            dispatch(Logout(noop))
            nav('/')
        }
    }

    const onCreateSuccess = async (msg) => {
        dispatch(onSuccessAlert(t(msg)))
        setIsAddressOpen(false)

        let formData = {}
        if (pageFormData) {
            formData = pageFormData
        } else {
            return nav('/')
        }
        setPageLoading(true)
        await dispatch(getMultiAddressList(formData, ()=>{setPageLoading(false)}, onError))

    }
    const onSubmit = (data) => {
        if(address?.trim() ===''){
            return setError("address1",{ type: "address1", message: t('CustomerPage.Address is required') })
        }
        if(cityValue?.trim() === ''){
            return setError("city",{ type: "city", message: t('CustomerPage.City is required') })
        }

        if(provinceValue?.trim() === ''){
            return setError("province",{ type: "province", message: t('CustomerPage.Province is required') })
        }

        if(postcodeValue?.trim() === ''){
            return setError("postcode",{ type: "postcode", message: t('CustomerPage.Postal Code is required') })
        }

        if(countryValue?.trim() === ''){
            return setError("country",{ type: "country", message: t('CustomerPage.Country is required') })
        }

        if(phone?.length < 14){
            return  setError("phoneNumber",{ type: "phoneNumber", message:t('CustomerPage.Please input valid phone number')})
        }

        let record = {}

        record.address1 = address?.trim()
        record.address2 = data?.address2?.trim()
        record.city = cityValue?.trim()
        record.country = countryValue?.trim()
        record.isDefault = isDefault? "Y" : "N"
        record.name = data.fullName?.trim()
        record.phone = phone?.trim().replace(/ /g, '')
        record.province = provinceValue?.trim()
        record.zipcode = postcodeValue?.trim()
        record.userName = LocalStorage.getItem("username")


        if(mode === 'Add'){
            dispatch(addMultiAddress({address:record},onCreateSuccess,onError))
        }

        if (mode === 'Edit'){
            record.addressLineNo = currentAddress?.addressLineNo
            record.ownerId = currentAddress?.ownerId
            record.ownerName = currentAddress?.ownerName
            record.provinceCopy = "-"
            dispatch(modifyMultiAddress({address:record},onCreateSuccess,onError))
        }

        handleClearAll()
    }

    const handleSelect = async address => {
        const results = await geocodeByAddress(address)
        let address1 = "";
        let postcode = "";
        let city = '';
        let state = '';
        let country = '';
        for(const component of results[0].address_components){
            const componentType = component.types[0];

            switch (componentType) {
                case "street_number": {
                    address1 = `${component.long_name} ${address1}`;
                    break;
                }

                case "route": {
                    address1 += component.short_name;
                    break;
                }

                case "postal_code": {
                    postcode = `${component.long_name}${postcode}`;
                    break;
                }

                case "postal_code_suffix": {
                    postcode = `${postcode}-${component.long_name}`;
                    break;
                }

                case "locality":
                    city =
                        component.long_name;
                    break;

                case "administrative_area_level_1": {
                    state =
                        component.short_name;
                    break;
                }

                case "country":
                    country =
                        component.long_name;
                        // component.short_name.toLowerCase();
                    break;
            }
        }
        setAddress(address1)
        setCityValue(city)
        setProvinceValue(state)
        setCountryValue(country)
        setPostcodeValue(postcode)
        document.getElementById('address2').focus()
    };

    const handleMouseEnter = (index) => {
        setHoverSelect(index);
    };

    const handleMouseLeave = () => {
        setHoverSelect(-1);
    };

    useEffect(() => {
        if(mode === 'Edit' || mode === 'Preview'){
            setIsAddressOpen(true)
            setValue('fullName',currentAddress?.name)
            setValue('address2',currentAddress?.address2)
            setAddress(currentAddress?.address1)
            setCityValue(currentAddress?.city)
            setProvinceValue(currentAddress?.province)
            setPhone(currentAddress?.phone)
            setCountryValue(currentAddress?.country)
            setPostcodeValue(currentAddress?.zipcode)
            setIsDefault(currentAddress?.isDefault === 'Y')


        }
    }, [currentAddress])


    return(<Dialog
        open={isAddressOpen}
        onClose={handleClearAll}
        PaperComponent={PaperComponent}
        aria-labelledby="draggable-dialog-title"
    >
        <DialogTitle id="draggable-dialog-title" style={{ cursor: 'move' }}>
            <MDTypography variant={'h5'}>
                {mode === "Add"?t("AccountPage.Add New Address"):t("AccountPage.Edit Address")}
            </MDTypography>
        </DialogTitle>
        <MDBox component="form" role="form" onSubmit={handleSubmit(onSubmit)} noValidate sx={{maxWidth: 1300}}>
            <DialogContent dividers={scroll === 'paper'}>
                <DialogContentText
                    id="scroll-dialog-description"
                    tabIndex={-1}
                >
                    <MDBox pt={2} pb={2} px={3} sx={{display: 'flex', justifyContent: 'space-between'}}>
                        <MDBox sx={{display: 'flex', flexDirection:'column',justifyContent: 'space-between'}}>
                            <Grid textAlign='center'>
                                <Icon style={darkMode?{color:'white'}:{color:'text'}}  fontSize={'large'}>account_circle</Icon></Grid>
                            <MDTypography variant={'h5'} align="center">{t("AccountPage.Address Info")}</MDTypography>
                            <MDBox mb={2}>
                                <FormControl
                                    error = {!!errors.fullName}
                                    fullWidth
                                    focused = {true}
                                >
                                    <MDInput
                                        type="text"
                                        label={t("AccountPage.Full Name")}
                                        variant="standard"
                                        fullWidth
                                        required
                                        disabled={mode === 'Preview'}
                                        {...register("fullName")}
                                        error={!!errors?.fullName}
                                        InputLabelProps={{shrink: true}}
                                    />
                                    <FormHelperText sx={{fontWeight:'medium'}}>{errors?.fullName?.message}</FormHelperText>
                                </FormControl>
                            </MDBox>
                            <MDBox mb={2}>
                                <PlacesAutocomplete
                                    value={address}
                                    onChange={setAddress}
                                    onSelect={handleSelect}
                                    searchOptions={{
                                        language:languageSelect ==='zh'?'zh':'en',
                                        types: addressFilter,
                                        fields:["address_components"],
                                        componentRestrictions: {
                                            country: ['us','ca','cn'],
                                        },
                                    }}
                                    debounce={200}
                                >
                                    {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
                                        <FormControl
                                            error = {!!errors.address1}
                                            fullWidth
                                            required
                                            focused = {true}
                                        >
                                            <MDInput
                                                {...getInputProps()}
                                                inputProps={{
                                                    autocomplete: "new-password",
                                                }}
                                                type="text"
                                                label={t("AccountPage.Address 1")}
                                                variant="standard"
                                                fullWidth
                                                required
                                                InputLabelProps={{shrink: true}}
                                                disabled={mode === 'Preview'}
                                            />
                                            <FormHelperText sx={{fontWeight:'medium'}}>{errors?.address1?.message}</FormHelperText>
                                            <div>
                                                {loading && <div style={{fontSize:'small'}}>Loading...</div>}
                                                {suggestions.map((suggestion,index )=> (
                                                    <div {...getSuggestionItemProps(suggestion)}>
                                <span
                                    onMouseEnter={() => handleMouseEnter(index)}
                                    onMouseLeave={handleMouseLeave}
                                    style={{fontSize:'small',cursor:'pointer',fontStyle: index === hoverSelect?'italic' : 'normal'}}
                                >{suggestion.description}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </FormControl>

                                    )

                                    }

                                </PlacesAutocomplete>

                            </MDBox>
                            <MDBox mb={2}>
                                <FormControl
                                    error = {!!errors.address2}
                                    fullWidth
                                    focused = {true}
                                >
                                    <MDInput
                                        type="text"
                                        id={'address2'}
                                        inputProps={{
                                            autocomplete: "new-password",
                                        }}
                                        label={t("AccountPage.Apartment, unit, suite, or floor #")}
                                        variant="standard"
                                        fullWidth
                                        {...register("address2")}
                                        error={!!errors?.address2}
                                        InputLabelProps={{shrink: true}}
                                        disabled={mode === 'Preview'}
                                    />
                                    <FormHelperText sx={{fontWeight:'medium'}}>{errors?.address2?.message}</FormHelperText>
                                </FormControl>
                            </MDBox>
                            <MDBox mb={2}>
                                <FormControl
                                    error = {!!errors.city}
                                    fullWidth
                                    focused = {true}
                                    required
                                >
                                    <MDInput
                                        type="text"
                                        label={t("AccountPage.City")}
                                        variant='standard'
                                        fullWidth
                                        required
                                        name='city'
                                        inputProps={{
                                            autocomplete: "new-password",
                                        }}
                                        value={cityValue}
                                        onChange={(event)=>setCityValue(event.target.value)}
                                        error={!!errors?.city}
                                        InputLabelProps={{shrink: true}}
                                        disabled={mode === 'Preview'}
                                    />
                                    <FormHelperText sx={{fontWeight:'medium'}}>{errors?.city?.message}</FormHelperText>
                                </FormControl>
                            </MDBox>

                            <MDBox mb={2}>
                                <FormControl
                                    error = {!!errors.province}
                                    fullWidth
                                    required
                                >
                                    <MDInput
                                        type="text"
                                        label={t("AccountPage.State/Province")}
                                        variant='standard'
                                        fullWidth
                                        required
                                        name='province'
                                        inputProps={{
                                            autocomplete: "new-password",
                                        }}
                                        value={provinceValue}
                                        onChange={(event)=>setProvinceValue(event.target.value)}
                                        error={!!errors?.province}
                                        disabled={mode === 'Preview'}
                                    />
                                    <FormHelperText sx={{fontWeight:'medium'}}>{errors?.province?.message}</FormHelperText>
                                </FormControl>
                            </MDBox>
                            <Grid container mb={2} display="flex" xs={12} spacing={2}>
                                <Grid item xs={6} mb={2}>
                                    <FormControl
                                        error = {!!errors.postcode}
                                        fullWidth
                                        required
                                    >
                                        <MDInput
                                            type="text"
                                            label={t("AccountPage.Postal Code")}
                                            variant='standard'
                                            fullWidth
                                            required
                                            name='postcode'
                                            inputProps={{
                                                autocomplete: "new-password",
                                            }}
                                            value={postcodeValue}
                                            onChange={(event)=>setPostcodeValue(event.target.value)}
                                            error={!!errors?.postcode}
                                            disabled={mode === 'Preview'}
                                        />
                                        <FormHelperText sx={{fontWeight:'medium'}}>{errors?.postcode?.message}</FormHelperText>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={6} mb={2}>
                                    <FormControl
                                        error = {!!errors.country}
                                        fullWidth
                                        required
                                    >
                                        <MDInput
                                            type="text"
                                            label={t("AccountPage.Country")}
                                            variant='standard'
                                            fullWidth
                                            required
                                            name='country'
                                            inputProps={{
                                                autocomplete: "new-password",
                                            }}
                                            value={countryValue}
                                            onChange={(event)=>setCountryValue(event.target.value)}
                                            error={!!errors?.country}
                                            disabled={true}
                                        />
                                        <FormHelperText sx={{fontWeight:'medium'}}>{errors?.country?.message}</FormHelperText>
                                    </FormControl>
                                </Grid>
                            </Grid>

                            <Grid item xs={12} mb={2}>
                                <FormControl
                                    error = {!!errors.phoneNumber}
                                    fullWidth
                                    required={true}
                                >
                                    <MuiPhoneNumber
                                        fullWidth
                                        defaultCountry='ca'
                                        label={t("AccountPage.Phone Number")}
                                        required={true}
                                        value={phone}
                                        onChange={(value)=>setPhone(value)}
                                        disabled={mode === 'Preview'}
                                    />
                                    <FormHelperText sx={{fontWeight:'medium'}}>{errors?.phoneNumber?.message}</FormHelperText>

                                </FormControl>

                            </Grid>

                            <Grid container mb={2} display="flex" xs={12} spacing={2} alignItems={'center'}>
                                <Grid item>
                                    <MDTypography variant='button'>{t("AccountPage.Is Default")}</MDTypography>
                                </Grid>
                                <Grid item>

                                    <Checkbox
                                        size={'small'}
                                        disabled={mode === 'Preview'}
                                        checked={isDefault}
                                        onChange={(event) => {
                                            setIsDefault(event.target.checked)}}
                                        inputProps={{ 'aria-label': 'controlled' }}
                                    />

                                </Grid>
                            </Grid>
                        </MDBox>
                    </MDBox>
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <MDButton variant={'outlined'} color="primary" onClick={handleClearAll}>{t("Cancel")}</MDButton>
                {mode !=='Preview' && <MDButton variant={'contained'} color="primary" type={'submit'} >{t("Submit")}</MDButton>}
            </DialogActions>
        </MDBox>

    </Dialog>)
}

export default MultiaddressAdd